// Revisión automática de tramos (spec 2026-11-03): un tramo cuenta como
// revisado cuando una actuación ligada está en uno de los estados que la
// empresa ha configurado. Todo puro; la lectura de la config vive en el hook.
import type { LinkedWorkRef, RouteSegment } from '../types/surveillanceRoute';
import { liveLinkedBasicActionOrder } from './segmentLinkedBasicAction';
import { isSegmentReviewable } from './surveillanceRoute';

/** Clave de la configuración del tenant (lista separada por comas). */
export const SEGMENT_REVIEW_AUTO_STATES_KEY = 'segmentReviewAutoStates';

/**
 * Estados elegibles, en este orden. Sin «Abierta» (3-nov, decisión del
 * usuario): una actuación abierta aún no da el tramo por revisado. Fuera
 * también «Parcial» (borrador) y «Anulada» (no resuelve el tramo).
 */
export const SEGMENT_REVIEW_AUTO_SELECTABLE_STATES = [
  'validated',
  'pending-certification',
  'end',
] as const;

/** Valor por defecto cuando la clave no existe. */
export const DEFAULT_SEGMENT_REVIEW_AUTO_STATES: readonly string[] = [
  'pending-certification',
  'end',
];

/** Actuación que da el tramo por revisado. */
export type AutoReview = { roid: string | null; state: string };

/**
 * Interpreta la configuración: sin clave = defecto; vacía = apagado; el resto
 * se recorta, se filtra a lo elegible, se deduplica y se ordena como la lista.
 */
export function parseSegmentReviewAutoStates(
  raw: string | null | undefined
): string[] {
  if (raw === null || raw === undefined) return [...DEFAULT_SEGMENT_REVIEW_AUTO_STATES];
  const wanted = new Set(raw.split(',').map((part) => part.trim()));
  return SEGMENT_REVIEW_AUTO_SELECTABLE_STATES.filter((state) => wanted.has(state));
}

/**
 * Tramos revisados automáticamente. Solo los revisables; vale la básica viva
 * (la anulada no cuenta) o un trabajo del aviso de tipo actuación (los
 * accidentes no). La primera que cumple da el roid y el estado.
 */
export function computeAutoReviews(
  segments: RouteSegment[],
  worksBySegmentId: Record<string, LinkedWorkRef[]> | null | undefined,
  states: readonly string[]
): ReadonlyMap<string, AutoReview> {
  const result = new Map<string, AutoReview>();
  if (states.length === 0) return result;
  for (const segment of segments) {
    if (!isSegmentReviewable(segment)) continue;
    const basic = liveLinkedBasicActionOrder(segment.linkedBasicActionOrder);
    const candidates: Array<{ roid?: string | null; state?: string | null }> = [];
    // Igual que la tabla: la básica solo se pinta con `actionId` y los trabajos
    // del aviso solo con la OT sala (`linkedExternalOrder.id`).
    if (basic?.actionId) candidates.push(basic);
    if (segment.linkedExternalOrder?.id) {
      for (const work of worksBySegmentId?.[segment.id] ?? []) {
        if (work.type === 'action') candidates.push(work);
      }
    }
    for (const candidate of candidates) {
      const state = candidate.state;
      if (state && states.includes(state)) {
        result.set(segment.id, { roid: candidate.roid ?? null, state });
        break;
      }
    }
  }
  return result;
}
